<template>
  <div id="app" class="app-layout">
    <template v-if="!isLoginPage">
      <aside
        class="app-sidebar"
        :class="{ collapsed: sidebarCollapsed }"
        :style="{ width: sidebarCollapsed ? sidebarCollapsedWidth + 'px' : sidebarWidth + 'px' }"
      >
        <div class="sidebar-header">
          <span v-if="!sidebarCollapsed" class="logo-text">湃云团测</span>
          <el-button
            type="text"
            class="toggle-btn"
            :icon="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"
            @click="toggleSidebar"
          />
        </div>
        <el-menu
          :default-active="activeMenu"
          :collapse="sidebarCollapsed"
          :collapse-transition="false"
          background-color="#1a3a52"
          text-color="#b0c4d4"
          active-text-color="#188EFC"
          router
        >
          <el-menu-item index="/planList">
            <i class="el-icon-edit-outline"></i>
            <span slot="title">测评管理</span>
          </el-menu-item>
          <el-menu-item index="/sheetList">
            <i class="el-icon-document"></i>
            <span slot="title">测评列表</span>
          </el-menu-item>
          <el-menu-item index="/planProgress">
            <i class="el-icon-data-line"></i>
            <span slot="title">测评进度</span>
          </el-menu-item>
          <el-menu-item index="/planStatistics">
            <i class="el-icon-pie-chart"></i>
            <span slot="title">统计分析</span>
          </el-menu-item>
          <el-menu-item index="/scaleRule">
            <i class="el-icon-setting"></i>
            <span slot="title">筛查规则</span>
          </el-menu-item>
          <el-menu-item index="/profileField">
            <i class="el-icon-tickets"></i>
            <span slot="title">登录填写项</span>
          </el-menu-item>
        </el-menu>
      </aside>
      <section class="app-main" :style="{ marginLeft: (sidebarCollapsed ? sidebarCollapsedWidth : sidebarWidth) + 'px' }">
        <header class="main-header">
          <span class="page-title">{{ pageTitle }}</span>
          <div class="header-right">
            <!-- 头像图标 -->
            <div class="header-left">
              <el-avatar :size="28" :src="userInfo.avatar">
                {{ userInfo.userName.charAt(0) }}
              </el-avatar>
            </div>
            <el-dropdown trigger="click" @command="handleUserCommand">
              <span class="user-dropdown-trigger">
                {{ userInfo.userName || '管理员' }}
                <i class="el-icon-arrow-down el-icon--right" />
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="changePassword">
                  <i class="el-icon-edit" /> 修改密码
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <i class="el-icon-switch-button" /> 退出
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </header>
        <main class="main-content">
          <router-view />
        </main>
      </section>
    </template>
    <router-view v-else />

    <!-- 修改密码弹窗 -->
    <el-dialog
      title="修改密码"
      :visible.sync="passwordDialogVisible"
      width="420px"
      append-to-body
      @close="resetPasswordForm"
    >
      <el-form
        ref="passwordForm"
        :model="passwordForm"
        :rules="passwordRules"
        label-width="90px"
      >
        <el-form-item label="当前密码" prop="oldPassword">
          <el-input
            v-model="passwordForm.oldPassword"
            type="password"
            placeholder="请输入当前密码"
            show-password
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="请输入新密码（至少6位）"
            show-password
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
            autocomplete="off"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="passwordSubmitting" @click="submitPassword">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'App',
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的新密码不一致'))
      } else {
        callback()
      }
    }
    return {
      sidebarWidth: 150,
      sidebarCollapsedWidth: 64,
      sidebarCollapsed: false,
      passwordDialogVisible: false,
      passwordSubmitting: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '新密码至少6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState('user', ['token', 'userInfo']),
    isLoginPage() {
      return this.$route.path === '/login'
    },
    activeMenu() {
      const path = this.$route.path
      if (path.startsWith('/planList')) return '/planList'
      if (path.startsWith('/sheetList')) return '/sheetList'
      if (path.startsWith('/sheet')) return '/planList'
      if (path.startsWith('/planProgress')) return '/planProgress'
      if (path.startsWith('/planStatistics')) return '/planStatistics'
      if (path.startsWith('/scaleRule')) return '/scaleRule'
      if (path.startsWith('/profileField')) return '/profileField'
      return path
    },
    pageTitle() {
      const path = this.$route.path
      const map = {
        '/planList': '计划管理',
        '/sheetList': '测评列表',
        '/planProgress': '测评进度',
        '/planStatistics': '统计分析',
        '/scaleRule': '筛查规则',
        '/profileField': '登录填写项'
      }
      if (map[path]) return map[path]
      if (path.startsWith('/planList')) return '计划管理'
      if (path.startsWith('/sheetList')) return '测评列表'
      if (path.startsWith('/planProgress')) return '测评进度'
      if (path.startsWith('/planStatistics')) return '统计分析'
      if (path.startsWith('/scaleRule')) return '筛查规则'
      if (path.startsWith('/profileField')) return '登录填写项'
      return ' '
    }
  },
  created() {
    const collapsed = localStorage.getItem('paiyun-sidebar-collapsed')
    if (collapsed === '1') this.sidebarCollapsed = true
    if (this.token) this.getInfo()
  },
  methods: {
    ...mapActions('user', ['logout', 'getInfo', 'changePassword']),
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      localStorage.setItem('paiyun-sidebar-collapsed', this.sidebarCollapsed ? '1' : '0')
    },
    handleUserCommand(command) {
      if (command === 'changePassword') {
        this.passwordDialogVisible = true
      } else if (command === 'logout') {
        this.logout()
        this.$router.replace('/login').catch(() => {})
      }
    },
    resetPasswordForm() {
      this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
    },
    submitPassword() {
      this.$refs.passwordForm.validate(valid => {
        if (!valid) return
        this.passwordSubmitting = true
        this.changePassword({
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword
        })
          .then((res) => {
            if (res && res.code === 200) {
              this.$message.success('密码修改成功，请重新登录')
              this.passwordDialogVisible = false
              this.logout()
              this.$router.replace('/login').catch(() => {})
            }
          })
          .finally(() => {
            this.passwordSubmitting = false
          })
      })
    }
  }
}
</script>

<style lang="scss">
@import './styles/variables.scss';

.app-layout {
  min-height: 100vh;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  background: $bg-page;
}

.app-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  background: #1a3a52;
  transition: width 0.2s;

  .sidebar-header {
    height: 56px;
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    .logo-text {
      font-size: 16px;
      font-weight: 500;
      color: #fff;
      flex: 1;
    }

    .toggle-btn {
      color: #b0c4d4;
      font-size: 18px;
      padding: 4px;
    }
  }

  .el-menu {
    border-right: none;
  }

  .el-menu-item.is-active {
    background-color: rgba(24, 142, 252, 0.2) !important;
    color: #188EFC;
  }
}

.app-main {
  min-height: 100vh;
  transition: margin-left 0.2s;
}

.main-header {
  height: 56px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid $border-line;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .page-title {
    font-size: 20px;
    font-weight: 500;
    color: $text-title;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 8px;

    .header-left {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      cursor: pointer;
    }

    .el-dropdown {
      display: flex;
      align-items: center;
    }

    .user-dropdown-trigger {
      display: inline-flex;
      align-items: center;
      line-height: 1;
      color: $text-secondary;
      font-size: 14px;
      cursor: pointer;

      .el-icon--right {
        margin-left: 4px;
        font-size: 12px;
      }
    }
  }
}

.main-content {
  padding: 10px;
  background: $bg-page;
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
}
</style>
